import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useAppDispatch } from '../../store';
import { Note } from '../../types';
import {
  deleteNoteAsync,
  toggleNotePinnedAsync,
  toggleNoteFavoriteAsync,
  toggleNoteArchivedAsync
} from '../../store/slices/notesSlice';
import {
  X,
  Edit,
  Pin,
  Archive,
  Copy,
  Share2,
  Download,
  Maximize2,
  Minimize2,
  Search,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronRight,
  Heart,
  Star,
  Tag,
  Calendar,
  Clock,
  User,
  BookOpen,
  FileText,
  Image,
  Link,
  Hash,
  Type,
  AlignLeft,
  List,
  CheckSquare,
  Quote,
  Code,
  Highlighter,
  Palette,
  Settings,
  Filter,
  SortAsc,
  MoreHorizontal
} from 'lucide-react';

// Custom hook for intersection observer
const useIntersectionObserver = (options: IntersectionObserverInit = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
      if (entry.isIntersecting && !hasIntersected) {
        setHasIntersected(true);
      }
    }, options);

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, [hasIntersected, options]);

  return [targetRef, isIntersecting, hasIntersected] as const;
};

// Lazy loaded section component
interface LazySectionProps {
  section: ParsedSection;
  index: number;
  renderSectionFn: (section: ParsedSection, index: number) => React.ReactNode;
  fontSize: number;
}

const LazySection = React.memo<LazySectionProps>(({ section, index, renderSectionFn, fontSize }) => {
  const [ref, isIntersecting, hasIntersected] = useIntersectionObserver();
  
  return (
    <div ref={ref} className="min-h-[50px]">
      {hasIntersected ? (
        renderSectionFn(section, index)
      ) : (
        <div className="flex items-center justify-center py-8 text-gray-400 dark:text-gray-500">
          <div className="animate-pulse flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
            <div className="text-sm">İçerik yükleniyor...</div>
          </div>
        </div>
      )}
    </div>
  );
});

interface NoteDetailProps {
  note: Note;
  onClose: () => void;
  onEdit?: () => void;
}

interface ParsedSection {
  type: 'header' | 'paragraph' | 'list' | 'code' | 'quote' | 'divider' | 'image' | 'link' | 'table';
  content: string;
  level?: number;
  items?: string[];
  language?: string;
  url?: string;
  alt?: string;
  headers?: string[];
  rows?: string[][];
}

interface SubjectInfo {
  name: string;
  color: string;
  bgColor: string;
  description: string;
}

const NoteDetail: React.FC<NoteDetailProps> = React.memo(({ note, onClose, onEdit }) => {
  const dispatch = useAppDispatch();
  const [showMenu, setShowMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [readingMode, setReadingMode] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());
  const [selectedText, setSelectedText] = useState('');
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [showTagSearch, setShowTagSearch] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Subject information mapping
  const subjectInfo: Record<string, SubjectInfo> = {
    matematik: {
      name: 'Matematik',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      description: 'Sayılar, geometri ve problem çözme'
    },
    turkce: {
      name: 'Türkçe',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      description: 'Dil bilgisi, edebiyat ve yazım kuralları'
    },
    fen: {
      name: 'Fen Bilimleri',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      description: 'Fizik, kimya, biyoloji ve doğa bilimleri'
    },
    sosyal: {
      name: 'Sosyal Bilgiler',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      description: 'Tarih, coğrafya ve vatandaşlık'
    },
    ingilizce: {
      name: 'İngilizce',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      description: 'Yabancı dil öğrenimi ve iletişim'
    },
    din: {
      name: 'Din Kültürü',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      description: 'Ahlak, değerler ve din bilgisi'
    }
  };

  const currentSubject = subjectInfo[note.subjectId || ''] || {
    name: note.subjectId || 'Bilinmiyor',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    description: 'Genel konu'
  };

  // Parse note content into sections
  const parseContent = useCallback((content: string): ParsedSection[] => {
    const lines = content.split('\n');
    const sections: ParsedSection[] = [];
    let currentSection: ParsedSection | null = null;
    let inCodeBlock = false;
    let codeLanguage = '';
    let codeContent: string[] = [];

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Code blocks
      if (trimmedLine.startsWith('```')) {
        if (inCodeBlock) {
          // End code block
          if (currentSection) {
            currentSection.content = codeContent.join('\n');
            sections.push(currentSection);
          }
          inCodeBlock = false;
          currentSection = null;
          codeContent = [];
        } else {
          // Start code block
          inCodeBlock = true;
          codeLanguage = trimmedLine.slice(3).trim();
          currentSection = {
            type: 'code',
            content: '',
            language: codeLanguage || 'text'
          };
        }
        continue;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        continue;
      }

      // Headers
      if (trimmedLine.startsWith('#')) {
        if (currentSection) sections.push(currentSection);
        const level = trimmedLine.match(/^#+/)?.[0].length || 1;
        currentSection = {
          type: 'header',
          content: trimmedLine.replace(/^#+\s*/, ''),
          level
        };
        sections.push(currentSection);
        currentSection = null;
        continue;
      }

      // Dividers
      if (trimmedLine === '---' || trimmedLine === '***') {
        if (currentSection) sections.push(currentSection);
        sections.push({ type: 'divider', content: '' });
        currentSection = null;
        continue;
      }

      // Lists
      if (trimmedLine.match(/^[\-\*\+]\s/) || trimmedLine.match(/^\d+\.\s/)) {
        if (currentSection?.type !== 'list') {
          if (currentSection) sections.push(currentSection);
          currentSection = { type: 'list', content: '', items: [] };
        }
        currentSection.items?.push(trimmedLine.replace(/^[\-\*\+\d+\.\s]+/, ''));
        continue;
      }

      // Quotes
      if (trimmedLine.startsWith('>')) {
        if (currentSection?.type !== 'quote') {
          if (currentSection) sections.push(currentSection);
          currentSection = { type: 'quote', content: '' };
        }
        const quoteText = trimmedLine.replace(/^>\s*/, '');
        currentSection.content += (currentSection.content ? '\n' : '') + quoteText;
        continue;
      }

      // Images
      const imageMatch = trimmedLine.match(/!\[([^\]]*)\]\(([^\)]+)\)/);
      if (imageMatch) {
        if (currentSection) sections.push(currentSection);
        sections.push({
          type: 'image',
          content: imageMatch[2],
          alt: imageMatch[1]
        });
        currentSection = null;
        continue;
      }

      // Links
      const linkMatch = trimmedLine.match(/^\[([^\]]*)\]\(([^\)]+)\)$/);
      if (linkMatch) {
        if (currentSection) sections.push(currentSection);
        sections.push({
          type: 'link',
          content: linkMatch[1],
          url: linkMatch[2]
        });
        currentSection = null;
        continue;
      }

      // Empty lines
      if (!trimmedLine) {
        if (currentSection) {
          sections.push(currentSection);
          currentSection = null;
        }
        continue;
      }

      // Regular paragraphs
      if (currentSection?.type !== 'paragraph') {
        if (currentSection) sections.push(currentSection);
        currentSection = { type: 'paragraph', content: '' };
      }
      currentSection.content += (currentSection.content ? '\n' : '') + line;
    }

    if (currentSection) sections.push(currentSection);
    return sections;
  }, []);

  const sections = useMemo(() => parseContent(note.content), [note.content, parseContent]);

  // Search functionality
  const filteredSections = useMemo(() => {
    if (!searchTerm) return sections;
    return sections.filter(section => {
      const content = section.content.toLowerCase();
      const items = section.items?.join(' ').toLowerCase() || '';
      return content.includes(searchTerm.toLowerCase()) || items.includes(searchTerm.toLowerCase());
    });
  }, [sections, searchTerm]);

  // Tag filtering
  const filteredTags = useMemo(() => {
    if (!tagSearchTerm) return note.tags;
    return note.tags.filter(tag => 
      tag.toLowerCase().includes(tagSearchTerm.toLowerCase())
    );
  }, [note?.tags, tagSearchTerm]);

  const renderSection = useCallback((section: ParsedSection, index: number) => {
     const isCollapsed = collapsedSections.has(index);
     const content = processText(section.content);

     switch (section.type) {
       case 'divider':
         return (
           <div key={index} className="mb-6">
             <hr className="border-gray-300 dark:border-gray-600" />
           </div>
         );

       case 'header':
         const HeaderTag = `h${Math.min(section.level || 1, 6)}` as keyof JSX.IntrinsicElements;
         const headerClasses = {
           1: 'text-3xl font-bold text-gray-900 dark:text-white mb-4',
           2: 'text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-3',
           3: 'text-xl font-medium text-gray-700 dark:text-gray-200 mb-3',
           4: 'text-lg font-medium text-gray-700 dark:text-gray-200 mb-2',
           5: 'text-base font-medium text-gray-600 dark:text-gray-300 mb-2',
           6: 'text-sm font-medium text-gray-600 dark:text-gray-300 mb-2'
         };

         return (
           <div key={index} className="mb-4">
             <div className="flex items-center gap-2 group">
               <button
                 onClick={() => toggleSection(index)}
                 className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                 aria-label={isCollapsed ? 'Bölümü genişlet' : 'Bölümü daralt'}
               >
                 {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
               </button>
               <HeaderTag 
                 className={headerClasses[section.level as keyof typeof headerClasses] || headerClasses[1]}
                 style={{ fontSize: `${fontSize * (2 - (section.level || 1) * 0.2)}px` }}
               >
                 {content}
               </HeaderTag>
             </div>
           </div>
         );

       case 'paragraph':
         return (
           <div key={index} className={`mb-4 ${isCollapsed ? 'hidden' : ''}`}>
             <p 
               className="text-gray-700 dark:text-gray-300 leading-relaxed"
               style={{ fontSize: `${fontSize}px`, lineHeight: 1.6 }}
             >
               {content}
             </p>
           </div>
         );

       case 'list':
         return (
           <div key={index} className={`mb-4 ${isCollapsed ? 'hidden' : ''}`}>
             <ul className="space-y-2 ml-4">
               {section.items?.map((item, itemIndex) => (
                 <li key={itemIndex} className="flex items-start gap-2">
                   <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                   <span 
                     className="text-gray-700 dark:text-gray-300"
                     style={{ fontSize: `${fontSize}px` }}
                   >
                     {processText(item)}
                   </span>
                 </li>
               ))}
             </ul>
           </div>
         );

       case 'code':
         return (
           <div key={index} className={`mb-4 ${isCollapsed ? 'hidden' : ''}`}>
             <div className="bg-gray-900 dark:bg-gray-800 rounded-lg overflow-hidden">
               <div className="flex items-center justify-between px-4 py-2 bg-gray-800 dark:bg-gray-700">
                 <span className="text-sm text-gray-300">{section.language || 'text'}</span>
                 <button
                   onClick={() => copyToClipboard(section.content)}
                   className="text-gray-400 hover:text-white transition-colors"
                   aria-label="Kodu kopyala"
                 >
                   <Copy className="w-4 h-4" />
                 </button>
               </div>
               <pre 
                 className="p-4 text-gray-100 overflow-x-auto"
                 style={{ fontSize: `${fontSize * 0.9}px` }}
               >
                 <code>{section.content}</code>
               </pre>
             </div>
           </div>
         );

       case 'quote':
         return (
           <div key={index} className={`mb-4 ${isCollapsed ? 'hidden' : ''}`}>
             <blockquote className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-r">
               <p 
                 className="text-gray-700 dark:text-gray-300 italic"
                 style={{ fontSize: `${fontSize}px` }}
               >
                 {content}
               </p>
             </blockquote>
           </div>
         );

       case 'image':
         return (
           <div key={index} className={`mb-4 ${isCollapsed ? 'hidden' : ''}`}>
             <div className="rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
               <img 
                 src={section.content} 
                 alt={section.alt || 'Görsel'}
                 className="w-full h-auto"
                 loading="lazy"
               />
               {section.alt && (
                 <div className="p-2 text-sm text-gray-600 dark:text-gray-400 text-center">
                   {section.alt}
                 </div>
               )}
             </div>
           </div>
         );

       case 'link':
         return (
           <div key={index} className={`mb-4 ${isCollapsed ? 'hidden' : ''}`}>
             <a 
               href={section.url} 
               target="_blank" 
               rel="noopener noreferrer"
               className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
               style={{ fontSize: `${fontSize}px` }}
             >
               <Link className="w-4 h-4" />
               {section.content}
             </a>
           </div>
         );

       default:
         return (
           <div key={index} className={`mb-4 ${isCollapsed ? 'hidden' : ''}`}>
             <p 
               className="text-gray-700 dark:text-gray-300"
               style={{ fontSize: `${fontSize}px` }}
             >
               {content}
             </p>
           </div>
         );
     }
   }, [collapsedSections, fontSize]);

  // Text processing for highlighting and formatting
  const processText = useCallback((text: string) => {
    if (!text) return text;
    
    // Highlight search terms
    if (searchTerm) {
      const regex = new RegExp(`(${searchTerm})`, 'gi');
      return text.split(regex).map((part, index) => 
        regex.test(part) ? (
          <mark key={index} className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
            {part}
          </mark>
        ) : part
      );
    }
    
    return text;
  }, [searchTerm]);

  // Utility functions
  const toggleSection = useCallback((index: number) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, []);

  const handleDelete = useCallback(() => {
    if (window.confirm('Bu notu silmek istediğinizden emin misiniz?')) {
      dispatch(deleteNoteAsync(note.id));
      onClose();
    }
  }, [dispatch, note.id, onClose]);

  const handleTogglePinned = useCallback(() => {
    dispatch(toggleNotePinnedAsync(note.id));
  }, [dispatch, note.id]);

  const handleToggleFavorite = useCallback(() => {
    dispatch(toggleNoteFavoriteAsync(note.id));
  }, [dispatch, note.id]);

  const handleToggleArchived = useCallback(() => {
    dispatch(toggleNoteArchivedAsync(note.id));
  }, [dispatch, note.id]);

  const formatDate = useCallback((date: string | Date) => {
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(date));
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'f':
            e.preventDefault();
            setShowSearch(true);
            setTimeout(() => searchInputRef.current?.focus(), 100);
            break;
          case '=':
          case '+':
            e.preventDefault();
            setFontSize(prev => Math.min(prev + 2, 24));
            break;
          case '-':
            e.preventDefault();
            setFontSize(prev => Math.max(prev - 2, 12));
            break;
          case '0':
            e.preventDefault();
            setFontSize(16);
            break;
        }
      }
      
      if (e.key === 'Escape') {
        if (showSearch) {
          setShowSearch(false);
          setSearchTerm('');
        } else if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, isFullscreen, onClose]);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${
      isFullscreen ? 'p-0' : ''
    }`}>
      <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col max-h-full ${
        isFullscreen ? 'w-full h-full rounded-none' : 'w-full max-w-4xl h-[90vh]'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-t-lg">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              currentSubject.bgColor
            } ${currentSubject.color}`}>
              {currentSubject.name}
            </div>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                {note.title}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {currentSubject.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search Toggle */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={`p-2 rounded-lg transition-colors ${
                showSearch 
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
              aria-label="Arama"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Font Size Controls */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setFontSize(prev => Math.max(prev - 2, 12))}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-600 dark:text-gray-400"
                aria-label="Yazı boyutunu küçült"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="px-2 text-sm text-gray-600 dark:text-gray-400 min-w-[2rem] text-center">
                {fontSize}
              </span>
              <button
                onClick={() => setFontSize(prev => Math.min(prev + 2, 24))}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-600 dark:text-gray-400"
                aria-label="Yazı boyutunu büyüt"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Reading Mode */}
            <button
              onClick={() => setReadingMode(!readingMode)}
              className={`p-2 rounded-lg transition-colors ${
                readingMode 
                  ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400' 
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}
              aria-label="Okuma modu"
            >
              {readingMode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400 transition-colors"
              aria-label={isFullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>

            {/* Actions Menu */}
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400 transition-colors"
                aria-label="Menü"
              >
                <MoreHorizontal className="w-5 h-5" />
              </button>

              {showMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10">
                  <button
                    onClick={() => { onEdit?.(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <Edit className="w-4 h-4" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => { handleTogglePinned(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <Pin className={`w-4 h-4 ${note.isPinned ? 'text-blue-500' : ''}`} />
                    {note.isPinned ? 'Sabitlemeyi Kaldır' : 'Sabitle'}
                  </button>
                  <button
                    onClick={() => { handleToggleFavorite(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <Heart className={`w-4 h-4 ${note.isFavorite ? 'text-red-500' : ''}`} />
                    {note.isFavorite ? 'Favorilerden Çıkar' : 'Favorilere Ekle'}
                  </button>
                  <button
                    onClick={() => { handleToggleArchived(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <Archive className={`w-4 h-4 ${note.isArchived ? 'text-orange-500' : ''}`} />
                    {note.isArchived ? 'Arşivden Çıkar' : 'Arşivle'}
                  </button>
                  <hr className="my-1 border-gray-200 dark:border-gray-600" />
                  <button
                    onClick={() => { copyToClipboard(note.content); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <Copy className="w-4 h-4" />
                    Kopyala
                  </button>
                  <button
                    onClick={() => { /* Share functionality */ setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  >
                    <Share2 className="w-4 h-4" />
                    Paylaş
                  </button>
                  <hr className="my-1 border-gray-200 dark:border-gray-600" />
                  <button
                    onClick={() => { handleDelete(); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2 text-red-600 dark:text-red-400"
                  >
                    <X className="w-4 h-4" />
                    Sil
                  </button>
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-600 dark:text-gray-400 transition-colors"
              aria-label="Kapat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Notlarda ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Main Content */}
          <div className={`flex-1 overflow-y-auto ${
            readingMode ? 'bg-amber-50 dark:bg-amber-950/20' : 'bg-white dark:bg-gray-900'
          }`}>
            <div className="p-6" ref={contentRef}>
              {/* Note Meta Information */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Calendar className="w-4 h-4" />
                    <span>Oluşturulma: {formatDate(note.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>Güncellenme: {formatDate(note.updatedAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <User className="w-4 h-4" />
                    <span>Yazar: {note.lastEditedBy || 'Bilinmiyor'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <FileText className="w-4 h-4" />
                    <span>Kelime sayısı: {note.content.split(/\s+/).length}</span>
                  </div>
                </div>

                {/* Tags */}
                {note.tags && note.tags.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Etiketler:</span>
                      {note.tags.length > 3 && (
                        <button
                          onClick={() => setShowTagSearch(!showTagSearch)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          {showTagSearch ? 'Gizle' : 'Ara'}
                        </button>
                      )}
                    </div>
                    
                    {showTagSearch && (
                      <div className="mb-3">
                        <input
                          type="text"
                          placeholder="Etiketlerde ara..."
                          value={tagSearchTerm}
                          onChange={(e) => setTagSearchTerm(e.target.value)}
                          className="w-full px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      {filteredTags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Content Sections */}
              {filteredSections.length > 0 ? (
                <div className="space-y-6">
                  {filteredSections.map((section, index) => (
                    <LazySection
                      key={index}
                      section={section}
                      index={index}
                      renderSectionFn={renderSection}
                      fontSize={fontSize}
                    />
                  ))}
                </div>
              ) : searchTerm ? (
                <div className="text-center py-16">
                  <div className="text-gray-400 dark:text-gray-600 mb-4">
                    <Search className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Arama sonucu bulunamadı
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    "{searchTerm}" için herhangi bir sonuç bulunamadı.
                  </p>
                  <button
                    onClick={() => setSearchTerm('')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Aramayı temizle
                  </button>
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="text-gray-400 dark:text-gray-600 mb-4">
                    <FileText className="w-12 h-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    İçerik bulunamadı
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Bu not henüz içerik içermiyor.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

NoteDetail.displayName = 'NoteDetail';

export default NoteDetail;